import { Unarchive } from "@mui/icons-material";
import { Box, Typography, styled } from "@mui/material";
import _ from "lodash";
import { DateTime } from "luxon";
import { ReactElement, useMemo } from "react";

import {
  CampaignStatus,
  GetCampaignStatsForListQuery,
  ListCampaignsQuery,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import Table, {
  DeleteManyState,
  RowAction,
  TableColumns,
} from "~frontend/components/Table";
import {
  mapNameAndValueFromCampaignMetadata,
  NO_VALUE,
} from "~frontend/components/campaignMetadata/common";
import Archive from "~frontend/components/icons/Archive";
import Copy from "~frontend/components/icons/Copy";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme from "~frontend/theme";
import {
  confirmMessageArchive,
  confirmMessageUnarchive,
} from "~frontend/util/campaignActionConfirmMessages";

const CampaignName = styled("span")`
  font-size: 16px;
`;

const SecondaryText = styled("span")`
  font-size: 10px;
`;

type CampaignForList = ListCampaignsQuery["listCampaigns"]["campaigns"][0];
type CampaignStats =
  GetCampaignStatsForListQuery["listCampaigns"]["campaigns"][0];

function MetadataValueDetails({
  metadataValues,
}: {
  metadataValues: CampaignForList["metadata"];
}) {
  const sortedMetadataValues = metadataValues.toSorted((a, b) =>
    a.name.localeCompare(b.name)
  );
  const requiredFields = sortedMetadataValues.filter(field => field.required);
  const nonRequiredFields = sortedMetadataValues.filter(
    field => !field.required
  );
  // Map the values to a displayable format, filter any blank values, and
  // limit to 3. The required values will always show up first in the list.
  const allFieldValues = [...requiredFields, ...nonRequiredFields]
    .map(mapNameAndValueFromCampaignMetadata)
    .filter(v => v.value !== NO_VALUE);
  const displayValues = allFieldValues.slice(0, 3);
  const descriptionOfValues = `${displayValues.length} of ${allFieldValues.length} fields shown`;
  return (
    <FlexBox direction="column" gap={0.5} sx={{ mt: 0.5 }}>
      {displayValues.map(field => (
        <Typography key={field.name} variant="body3">
          {field.name}: {field.value}
        </Typography>
      ))}
      {allFieldValues.length > 3 && (
        <Typography variant="body3" color="textSecondary">
          {descriptionOfValues}
        </Typography>
      )}
    </FlexBox>
  );
}

function basicInfoForCampaign(campaign: CampaignForList): ReactElement {
  return (
    <Box sx={{ lineHeight: "110%" }}>
      <Typography
        variant="body3"
        component={"div"}
        sx={{ marginBottom: theme.spacing(0.5) }}
      >
        Campaign ID: #{campaign.shortId}
      </Typography>
      <SecondaryText>
        Created:{" "}
        {DateTime.fromMillis(campaign.createdAt).toLocaleString(
          DateTime.DATETIME_SHORT
        )}{" "}
        by {campaign.createdBy.fullName}
      </SecondaryText>
      {campaign.startedAt ? (
        <>
          <br />
          <SecondaryText>
            Started:{" "}
            {DateTime.fromMillis(campaign.startedAt).toLocaleString(
              DateTime.DATETIME_SHORT
            )}
          </SecondaryText>
        </>
      ) : null}
      {campaign.initialSendScheduleInfo ? (
        <>
          <br />
          <SecondaryText>
            {campaign.initialSendScheduleInfo.partialSendQty
              ? `Scheduled to send ${campaign.initialSendScheduleInfo.partialSendQty.toLocaleString()}: `
              : "Scheduled to send: "}
            {DateTime.fromMillis(
              campaign.initialSendScheduleInfo.scheduledFor
            ).toLocaleString(DateTime.DATETIME_SHORT)}
          </SecondaryText>
        </>
      ) : null}
      {campaign.metadata.length > 0 && (
        <MetadataValueDetails metadataValues={campaign.metadata} />
      )}
    </Box>
  );
}

function progressForCampaign(
  campaign: CampaignForList,
  stats?: CampaignStats
): string {
  if (!stats?.statsForList || !campaign.startedAt) {
    return "-";
  }

  if (campaign.status === CampaignStatus.InitialSendComplete) {
    return "100%";
  }

  const progressPct =
    (stats.statsForList.initialSentCount / stats.statsForList.contactCount) *
    100;
  return `${Math.floor(progressPct)}%`;
}

function needsResponseLinkForCampaign(
  campaign: CampaignForList,
  subaccountLink: (path: string) => string,
  stats?: CampaignStats
): ReactElement | null {
  if (!stats?.statsForList || !campaign.startedAt) {
    return null;
  }

  return (
    <InternalLink
      underline="always"
      to={subaccountLink(
        `/threads?campaign_ids=${campaign.id}&opt_out_status=NOT_OPTED_OUT&response_status=HAS_REPLIED_NEEDS_RESPONSE`
      )}
    >
      {stats.statsForList.threadsNeedingResponse.toLocaleString()}
    </InternalLink>
  );
}

function totalRaisedForCampaign(stats?: CampaignStats) {
  const totalRaisedInCents = stats?.statsForList?.totalRaisedInCents;
  if (totalRaisedInCents) {
    return (totalRaisedInCents / 100.0).toLocaleString("en-US", {
      style: "currency",
      currency: "USD",
    });
  }

  return "-";
}

function roi(stats?: CampaignStats) {
  const totalPriceInCents =
    (stats?.statsForList?.totalPriceInMillicents || 0) / 1000;
  const totalRaisedInCents = stats?.statsForList?.totalRaisedInCents || 0;

  if (totalPriceInCents && totalPriceInCents > 0) {
    const roi = totalRaisedInCents / totalPriceInCents;
    return `${(roi * 100).toFixed(1)}%`;
  }
  return "-";
}

type CampaignAction = "archive" | "unarchive" | "copy";

const actionsForStatus: { [K in CampaignStatus]: CampaignAction[] } = {
  [CampaignStatus.Draft]: ["archive", "copy"],
  [CampaignStatus.InitialSendReady]: ["archive", "copy"],
  [CampaignStatus.InitialSendCanceling]: ["copy"],
  [CampaignStatus.InitialSendRunning]: ["copy"],
  [CampaignStatus.InitialSendScheduled]: ["copy"],
  [CampaignStatus.InitialSendComplete]: ["archive", "copy"],
  [CampaignStatus.RoiTestAutosendPending]: ["archive", "copy"],
  [CampaignStatus.PartialSendEvaluationPending]: ["archive", "copy"],
  [CampaignStatus.Archived]: ["unarchive", "copy"],
};

export default function CampaignTable(
  props: {
    showFundraisingFields: boolean;
    showClicks: boolean;
    rows: CampaignForList[];
    campaignStats?: CampaignStats[] | null;
    handleArchive: (campaignId: string) => Promise<void>;
    handleUnarchive: (campaignId: string) => Promise<void>;
    handleCopy: (campaignId: string) => Promise<void>;
    deleteManyState: DeleteManyState;
    onChangeDeleteManyState: (newState: DeleteManyState) => void;
  } & (
    | {
        noFooter?: false;
        page: number;
        handleChangePage: (newPage: number) => void;
        rowsPerPage: number;
        handleChangeRowsPerPage: (newRowsPerPage: number) => void;
        totalCount: number;
      }
    | {
        noFooter: true;
      }
  )
): ReactElement {
  const {
    rows,
    campaignStats,
    handleArchive,
    handleUnarchive,
    handleCopy,
    noFooter,
    showFundraisingFields,
    showClicks,
  } = props;

  const { subaccountLink } = useSubaccount()!;

  const statsByCampaignId: {
    [campaignId: string]: CampaignStats | undefined;
  } | null = useMemo(
    () => (campaignStats ? _.keyBy(campaignStats, "id") : null),
    [campaignStats]
  );

  function getRowActions(
    campaign: CampaignForList
  ): RowAction<CampaignForList>[] {
    return actionsForStatus[campaign.status].map(
      (action: CampaignAction): RowAction<CampaignForList> => {
        switch (action) {
          case "archive":
            return {
              id: "archive",
              type: "button",
              icon: <Archive />,
              tooltipLabel: "Archive",
              confirm: {
                title: "Archive Campaign",
                content: confirmMessageArchive(campaign),
              },
              onClick: () => handleArchive(campaign.id),
            };
          case "unarchive":
            return {
              id: "unarchive",
              type: "button",
              icon: <Unarchive />,
              tooltipLabel: "Unarchive",
              confirm: {
                title: "Unarchive Campaign",
                content: confirmMessageUnarchive(campaign),
              },
              onClick: () => handleUnarchive(campaign.id),
            };
          case "copy":
            return {
              id: "copy",
              type: "button",
              icon: <Copy />,
              tooltipLabel: "Copy",
              onClick: () => handleCopy(campaign.id),
            };
        }
      }
    );
  }

  const columns: TableColumns<CampaignForList> = [
    {
      key: "name",
      title: "Campaign",
      align: "left",
      renderValue: campaign => (
        <FlexBox direction="column" gap={0.5}>
          <InternalLink
            underline="always"
            to={subaccountLink(
              campaign.startedAt === null
                ? `/campaigns/${campaign.shortId}/edit/basics`
                : `/campaigns/${campaign.shortId}`
            )}
          >
            <CampaignName>{campaign.name}</CampaignName>
          </InternalLink>

          {basicInfoForCampaign(campaign)}
        </FlexBox>
      ),
    },
    {
      key: "contacts",
      title: "Contacts",
      renderValue: campaign =>
        statsByCampaignId ? (
          (statsByCampaignId[
            campaign.id
          ]?.statsForList?.contactCount?.toLocaleString() ?? "-")
        ) : (
          <Loading size={30} />
        ),
    },
    {
      key: "progress",
      title: "Progress",
      renderValue: campaign =>
        statsByCampaignId ? (
          progressForCampaign(campaign, statsByCampaignId[campaign.id])
        ) : (
          <Loading size={30} />
        ),
    },
    {
      key: "needsResponse",
      title: "Needs Response",
      renderValue: campaign =>
        statsByCampaignId ? (
          needsResponseLinkForCampaign(
            campaign,
            subaccountLink,
            statsByCampaignId[campaign.id]
          )
        ) : (
          <Loading size={30} />
        ),
    },
  ];

  if (showFundraisingFields) {
    columns.push({
      key: "totalRaised",
      title: "Total Raised",
      renderValue: campaign =>
        statsByCampaignId?.[campaign.id] ? (
          totalRaisedForCampaign(statsByCampaignId[campaign.id])
        ) : (
          <Loading size={30} />
        ),
    });
  }

  if (showClicks) {
    columns.push({
      key: "clicks",
      title: "Clicks",
      renderValue: campaign =>
        statsByCampaignId ? (
          statsByCampaignId[campaign.id]?.statsForList?.clickCount || "-"
        ) : (
          <Loading size={30} />
        ),
    });
  }

  if (showFundraisingFields) {
    columns.push({
      key: "roi",
      title: "ROI",
      renderValue: campaign =>
        statsByCampaignId?.[campaign.id] ? (
          roi(statsByCampaignId[campaign.id])
        ) : (
          <Loading size={30} />
        ),
    });
  }

  if (noFooter) {
    return <Table columns={columns} rows={rows} noFooter />;
  } else {
    return (
      <Table
        columns={columns}
        rows={rows}
        totalCount={props.totalCount}
        rowsPerPage={props.rowsPerPage}
        onChangeRowsPerPage={props.handleChangeRowsPerPage}
        page={props.page}
        onChangePage={props.handleChangePage}
        rowActions={getRowActions}
        selectedRows={
          props.deleteManyState.selecting
            ? props.deleteManyState.selectedIds
            : null
        }
        onChangeSelectedRows={tagIds =>
          props.onChangeDeleteManyState({
            selecting: true,
            selectedIds: tagIds,
            deleting: false,
          })
        }
        selectDisabled={
          props.deleteManyState.selecting && props.deleteManyState.deleting
        }
        containerSx={{ overflowX: "visible" }}
      />
    );
  }
}
